import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { parseServerDate } from '../../utils/formatDate';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

function StatCard({ label, value, sub, color, icon }) {
  return (
    <div className="card" style={{ padding: '20px' }}>
      <div style={{ fontSize: 22, marginBottom: 8 }}>{icon}</div>
      <div style={{ fontFamily: 'var(--ff-m)', fontSize: 32, fontWeight: 500, color: color || 'var(--accent)', lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 5, fontWeight: 600 }}>{label}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

export default function AdminDashboard({ showToast }) {
  const navigate = useNavigate();
  const { authFetch } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    authFetch('/api/admin/stats').then(r => r.json()).then(d => { setStats(d); setLoading(false); }).catch(() => setLoading(false));
  }, [authFetch]);

  if (loading) return <div style={{ textAlign: 'center', padding: 60 }}><div className="loader" style={{ margin: '0 auto' }}></div></div>;
  if (!stats || stats.error) return <div className="card" style={{ color: 'var(--red)', padding: 24 }}>Failed to load admin stats: {stats?.error || 'Unknown error'}</div>;

  // Defensive defaults — stats.users may be undefined if DB has no data yet
  const users = stats.users || { total: 0, free: 0, pro: 0, enterprise: 0 };

  const planData = [
    { plan: 'Free', count: users.free, fill: 'var(--text3)' },
    { plan: 'Pro', count: users.pro, fill: 'var(--accent)' },
    { plan: 'Enterprise', count: users.enterprise, fill: 'var(--accent2)' },
  ];

  return (
    <div>
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div className="page-title">Admin Dashboard</div>
          <span className="admin-sidebar-badge">ADMIN ONLY</span>
        </div>
        <div className="page-sub">Platform overview — users, revenue, and usage metrics</div>
      </div>

      {/* Top KPIs */}
      <div className="g4" style={{ marginBottom: 24 }}>
        <StatCard icon="👥" label="Total Users" value={users.total} sub={`${users.pro} Pro · ${users.enterprise} Enterprise`} color="var(--accent)" />
        <StatCard icon="🏢" label="Companies" value={stats.companies} sub="Active workspaces" color="var(--green)" />
        <StatCard icon="📋" label="Assessments" value={stats.assessments} sub="Completed responses" color="var(--gold)" />
        <StatCard icon="💰" label="Total Revenue" value={`₹${(stats.revenue_inr || 0).toLocaleString('en-IN')}`} sub="All time" color="var(--cyan)" />
      </div>

      <div className="g2" style={{ marginBottom: 24 }}>
        <StatCard icon="🤖" label="AI Messages" value={stats.ai_messages} sub="Total sent by users" color="var(--accent2)" />
        <StatCard icon="⚡" label="Free → Pro Conv." value={users.total > 0 ? `${Math.round((users.pro / users.total) * 100)}%` : '0%'} sub="Conversion rate" color="var(--orange)" />
      </div>

      {/* Plan distribution chart */}
      <div className="g2" style={{ marginBottom: 24, alignItems: 'start' }}>
        <div className="card">
          <div className="card-title">Plan Distribution</div>
          <div style={{ height: 180 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={planData} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--bg-elevated)" />
                <XAxis dataKey="plan" tick={{ fill: 'var(--text2)', fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: 'var(--text3)', fontSize: 11 }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ background: 'var(--bg3)', border: '1px solid var(--border2)', borderRadius: 8, fontSize: 12 }} />
                <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                  {planData.map((entry, i) => (
                    <rect key={i} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div style={{ display: 'flex', gap: 16, marginTop: 12 }}>
            {planData.map(p => (
              <div key={p.plan} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div style={{ width: 8, height: 8, borderRadius: 2, background: p.fill }}></div>
                <span style={{ fontSize: 11, color: 'var(--text2)' }}>{p.plan}: {p.count}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="card">
          <div className="card-title">Recent Transactions</div>
          {stats.recent_transactions?.length === 0 ? (
            <div style={{ color: 'var(--text3)', fontSize: 13, padding: '20px 0' }}>No transactions yet</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              {(stats.recent_transactions || []).map(t => (
                <div key={t.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>{t.email}</div>
                    <div style={{ fontSize: 11, color: 'var(--text3)' }}>{t.description}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontFamily: 'var(--ff-m)', fontSize: 13, color: 'var(--green)' }}>₹{(t.amount / 100).toLocaleString('en-IN')}</div>
                    <div style={{ fontSize: 10, color: 'var(--text3)' }}>{parseServerDate(t.created_at).toLocaleDateString('en-IN')}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Recent signups */}
      <div className="sec-header">
        <div className="sec-title">Recent Signups</div>
        <div className="sec-line"></div>
        <button className="btn btn-ghost btn-sm" onClick={() => navigate('/admin/users')}>View All Users →</button>
      </div>
      <div className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: 24 }}>
        <table className="table">
          <thead><tr><th>Name</th><th>Email</th><th>Plan</th><th>Joined</th></tr></thead>
          <tbody>
            {(stats.recent_signups || []).map(u => (
              <tr key={u.id}>
                <td style={{ fontWeight: 600, color: 'var(--text)' }}>{u.name}</td>
                <td style={{ fontFamily: 'var(--ff-m)', fontSize: 12 }}>{u.email}</td>
                <td><span className={`badge plan-${u.plan}`} style={{ fontSize: 10 }}>● {u.plan?.toUpperCase()}</span></td>
                <td style={{ fontSize: 12, color: 'var(--text3)' }}>{parseServerDate(u.created_at).toLocaleDateString('en-IN')}</td>
              </tr>
            ))}
            {(stats.recent_signups || []).length === 0 && (
              <tr><td colSpan={4} style={{ textAlign: 'center', padding: 32, color: 'var(--text3)' }}>No users yet</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Quick actions */}
      <div className="sec-header"><div className="sec-title">Quick Actions</div><div className="sec-line"></div></div>
      <div className="g4">
        {[
          { icon: '👥', label: 'Manage Users', sub: 'View, edit plans, delete', action: () => navigate('/admin/users'), color: 'var(--gold-2)' },
          { icon: '📊', label: 'All Companies', sub: 'View all org workspaces', action: () => {}, color: 'var(--green)' },
          { icon: '💳', label: 'Transactions', sub: 'Revenue & payment history', action: () => {}, color: 'var(--gold)' },
          { icon: '⚙️', label: 'System Health', sub: 'Server status: ✅ OK', action: () => {}, color: 'var(--cyan)' },
        ].map(a => (
          <div key={a.label} className="card" style={{ cursor: 'pointer', transition: 'border-color 0.15s' }}
            onClick={a.action}
            onMouseEnter={e => e.currentTarget.style.borderColor = a.color}
            onMouseLeave={e => e.currentTarget.style.borderColor = ''}>
            <div style={{ fontSize: 24, marginBottom: 8 }}>{a.icon}</div>
            <div style={{ fontWeight: 600, fontSize: 14, color: a.color, marginBottom: 3 }}>{a.label}</div>
            <div style={{ fontSize: 12, color: 'var(--text3)' }}>{a.sub}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
